import type { TeamMember } from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns/format'

import { SafeFormattedText } from '@/components/ui/safe-formatted-message'
import type { MassMessage } from '@/modules/chatting/types'
import { MassMessageReassignDropdown } from '@/modules/chatting/ui/header/notifications/dialog/mass-messages-reassign-dropdown.tsx'
import { unsendMessageFx } from '@/modules/chatting/ui/sidebar/mass-messages-statistics/models/normalMassMessagesStats.ts'

import { MEDIA_TYPE_ICONS } from '../../consts'
import { UnsendButton } from '../components/UnsendButton.tsx'

export const getStatsTableColumns = (
  members: TeamMember[],
  isMembersFetching: boolean,
): ColumnDef<MassMessage>[] => [
  {
    accessorKey: 'date',
    header: 'Date & Time',
    size: 200,
    cell: ({ getValue }) => {
      return format(new Date(getValue<string>()), 'MMM dd,yyyy • HH:mm a')
    },
  },
  {
    accessorKey: 'text',
    header: 'Message',
    size: 360,
    enableSorting: false,
    cell: ({ getValue }) => <SafeFormattedText text={getValue<string>()} />,
  },
  {
    accessorKey: 'mediaTypes',
    header: 'Attachment',
    size: 120,
    enableSorting: false,
    cell: ({ getValue }) => {
      const medias = getValue<MassMessage['mediaTypes']>()

      if (!medias) return null

      return (
        <div className="flex gap-2">
          {Object.entries(medias).map(([type, count]) => {
            return (
              <div
                className="flex items-center gap-1.5"
                key={type}
              >
                {MEDIA_TYPE_ICONS[type as keyof typeof MEDIA_TYPE_ICONS].icon}
                <span>{count}</span>
              </div>
            )
          })}
        </div>
      )
    },
  },
  {
    accessorKey: 'price',
    header: 'Price',
    size: 80,
    enableSorting: false,
    cell: ({ getValue }) => (getValue() ? `$${getValue()}` : ''),
  },
  {
    accessorKey: 'sentCount',
    header: 'Sent',
    size: 80,
    enableSorting: false,
  },
  {
    accessorKey: 'viewedCount',
    header: 'Viewed',
    size: 80,
    enableSorting: false,
  },
  {
    accessorKey: 'purchasedCount',
    header: 'Purchased',
    size: 90,
    cell: ({ getValue }) => getValue() ?? '',
    enableSorting: false,
  },
  {
    accessorKey: 'canUnsend',
    header: 'Unsend',
    size: 120,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <UnsendButton
        canUnsend={getValue<boolean>()}
        id={row.original.id}
        sentCount={row.original.sentCount}
        mutationFn={unsendMessageFx}
      />
    ),
  },
  {
    accessorKey: 'userId',
    header: 'Sender',
    size: 120,
    enableSorting: false,
    cell: ({ row }) => (
      <MassMessageReassignDropdown
        massMessage={row.original}
        members={members}
        isMembersFetching={isMembersFetching}
      />
    ),
  },
]
